import { z } from 'zod';

import {
  CalcTypeTransform,
  CollectTypeTransform
} from 'reservations/booking/types';
import { defaultWhenNullish } from 'utility/zod';

const GoogleMapsStaticUrl = z.object({
  static_url_mobile: z.string(),
  static_url_desktop: z.string(),
  static_url_tablet: z.string(),
  lat: z.number(),
  lng: z.number()
});
type GoogleMapsStaticUrl = z.infer<typeof GoogleMapsStaticUrl>;

const LocationConfig = z.object({
  id: z.string().or(z.number()),
  lat: z.string().nullish(),
  lng: z.string().nullish(),
  street: z.string(),
  state: z.string(),
  postal_code: z.string(),
  city: z.string(),
  phone_number: defaultWhenNullish(z.string(), ''),
  email_address: defaultWhenNullish(z.string(), ''),
  name: z.string().default(''),
  slug: z.string(),
  google_maps_static_urls: GoogleMapsStaticUrl,
  order: z.number()
});
export type LocationConfig = z.infer<typeof LocationConfig>;

const FeePolicyConfig = z.object({
  shouldCollect: z.boolean(),
  calcType: CalcTypeTransform,
  collectType: CollectTypeTransform,
  fee: z.string(),
  minPartySize: z.number(),
  cancelDeadlineMinutes: z.number()
});

export type FeePolicyConfig = z.infer<typeof FeePolicyConfig>;

const BaseConfig = z.object({
  acceptsParties: z.boolean(),
  featureEnabled: z.boolean(),
  partySizeMax: z.number(),
  partySizeMin: z.number()
});

const BookingConfig = BaseConfig.extend({
  maxAdvanceDays: z.number(),
  minAdvanceMinutes: z.number(),
  slotSize: z.number()
});
export type BookingConfig = z.infer<typeof BookingConfig>;

const WaitlistConfig = BaseConfig.extend({ waitlist_closed_text: z.string() });
export type WaitlistConfig = z.infer<typeof WaitlistConfig>;

const ChecklessPaymentsConfig = z
  .object({
    default_tip: z.number(),
    is_checkless_payments_enabled: z.boolean().default(false),
    pre_auth_amount: z.string(),
    tip_amounts: z.array(z.string()),
    tip_available: z.boolean(),
    is_tip_editable_post_close: z.boolean()
  })
  .nullable();
export type ChecklessPayments = z.infer<typeof ChecklessPaymentsConfig>;

export const ConfigResponse = z.object({
  inGoodStanding: z.boolean(),
  ianaTimeZone: z.string().default(''),
  access_token: z.string(),
  booking: BookingConfig,
  feePolicy: FeePolicyConfig,
  location: LocationConfig,
  waitlist: WaitlistConfig,
  checkless_payments: ChecklessPaymentsConfig
});
export type Config = z.infer<typeof ConfigResponse>;

export const ConfigData = ConfigResponse.extend({ slug: z.string() });
export type ConfigProviderData = z.infer<typeof ConfigData>;
